import { useNavigate, useSearchParams } from 'react-router-dom';
import { BioSampleKitCode } from '@csp/csp-common-hbs-model';
import { Maybe, OrgId } from '@csp/csp-common-model';
import { useEffect, useState } from 'react';
import { template } from 'lodash';
import { LoadingErrorState, useBoolean } from '@csp/fe-hooks';
import { HbsSearchParamKey } from '../../type/HbsSearchParamKey';
import { BioSampleKitItem } from '../model/BioSampleKitItem';
import { BioSampleKitItemQueryService } from '../../service/BioSampleKitItemQueryService';
import { useStudyContext } from '../../../../common/context/study/useStudyContext';
import { useLoading } from '../../../../common/hooks/useLoading';
import { BioSampleContentService } from '../../service/BioSampleContentService';

type In = {
  kitCode: Maybe<BioSampleKitCode>;
};

type Out = LoadingErrorState & {
  isOpen: boolean;
  onClose(): void;
  kitItem: Maybe<BioSampleKitItem>;
  notFoundMessage: Maybe<string>;
};

export const useKit = ({ kitCode }: In): Out => {
  const { setLoading, isLoading, clearLoading, error } = useLoading();
  const [kitItem, setKitItem] = useState<Maybe<BioSampleKitItem>>();
  const [notFoundMessage, setNotFoundMessage] = useState<Maybe<string>>();
  const { study, studyConfig } = useStudyContext();
  const [isMounted, setHasMounted] = useBoolean(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const texts = BioSampleContentService.getKitDetailsTexts();

  const onClose = (): void => {
    const params = new URLSearchParams(searchParams);
    params.delete(HbsSearchParamKey.KIT_CODE);
    setSearchParams(params);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const isOpen = !!kitCode;

  useEffect(() => {
    setHasMounted();
    if (isMounted && !isOpen) {
      setKitItem(undefined);
    }
  }, [isMounted, isOpen, setHasMounted, setKitItem]);

  useEffect(() => {
    if (kitCode) {
      setLoading();

      const orgIds: OrgId[] = study.sites.map(site => site.orgId);
      BioSampleKitItemQueryService.fetchByKitCode({ kitCode, study, studyConfig, orgIds })
        .then(kitItem => {
          setKitItem(kitItem);
          setNotFoundMessage(
            kitItem ? undefined : template(texts.drawer.alertError.failedToLoadKit.text)({ code: kitCode }),
          );
          clearLoading();
        })
        .catch(error => clearLoading({ error }));
    }
  }, [kitCode, study, studyConfig, clearLoading, setLoading, texts.drawer.alertError.failedToLoadKit.text]);

  return {
    error,
    isLoading,
    isOpen,
    onClose,
    kitItem,
    notFoundMessage,
  };
};
